import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useFirebase } from '../contexts/FirebaseContext';
import { useAuth } from '../contexts/AuthContext';
import { useAddress } from '../contexts/AddressContext';
import { getStorage, ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { updateProfile as updateAuthProfile } from 'firebase/auth';
import {
  User, LogOut, MapPin, CreditCard, Plus, Star, Trash2, Camera, Pencil,
  BookOpen, HelpCircle, FileText, ShieldCheck, AlertTriangle, Settings,
  Bell, MoreHorizontal, Loader2,
} from 'lucide-react';
import { ChefHeader, ListSection, ListRow, Sheet } from '../components/ios';

// iOS-style toggle switch (mirrors SwiftUI Toggle)
function IosSwitch({ checked, onChange }) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      onClick={() => onChange(!checked)}
      className={`w-[51px] h-[31px] rounded-full p-[2px] transition-colors shrink-0 ${
        checked ? 'bg-[#34C759]' : 'bg-[#E9E9EB]'
      }`}
    >
      <span
        className={`block w-[27px] h-[27px] rounded-full bg-white shadow transition-transform ${
          checked ? 'translate-x-[20px]' : 'translate-x-0'
        }`}
      />
    </button>
  );
}

// LabeledContent row: label left, gray value right (no chevron)
function LabeledRow({ label, value }) {
  return (
    <div className="flex items-center justify-between gap-3 px-4 py-3 min-h-[44px]">
      <span className="text-[16px] text-gray-900">{label}</span>
      <span className="text-[15px] text-[#8E8E93] truncate">{value}</span>
    </div>
  );
}

function ProfilePage() {
  const { user, db } = useFirebase();
  const { logout } = useAuth();
  const { addresses, loading: addressesLoading, deleteAddress, updateAddress } = useAddress();
  const navigate = useNavigate();

  const [uploadingImage, setUploadingImage] = useState(false);
  const [userPhone, setUserPhone] = useState(null);
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [profileImageUrl, setProfileImageUrl] = useState(user?.photoURL || null);
  const [sheet, setSheet] = useState(null); // 'edit' | 'settings'
  const [menuFor, setMenuFor] = useState(null); // address id with open context menu
  const [savingProfile, setSavingProfile] = useState(false);
  const [editPhone, setEditPhone] = useState('');
  const [orderNotifications, setOrderNotifications] = useState(
    () => localStorage.getItem('orderNotifications') !== '0'
  );
  const fileInputRef = useRef(null);

  // Load user profile fields from Firestore (mirrors iOS ProfileViewModel.loadProfile)
  useEffect(() => {
    if (!user?.uid || !db) return;

    const loadUserProfile = async () => {
      try {
        const { doc, getDoc } = await import('firebase/firestore');
        const userDocRef = doc(db, 'users', user.uid);
        const userDoc = await getDoc(userDocRef);
        if (userDoc.exists()) {
          const userData = userDoc.data();
          // Check for various possible field names
          const phone = userData.phoneNumber || userData.phone || userData.mobile || null;
          setUserPhone(phone);
          setFirstName(userData.firstName || '');
          setLastName(userData.lastName || '');
        }
      } catch (error) {
        console.error('Error loading user profile:', error);
      }
    };

    loadUserProfile();
  }, [user?.uid, db]);

  const handleLogout = async () => {
    try {
      await logout();
      navigate('/');
    } catch (error) {
      console.error('Logout error:', error);
    }
  };

  const handleDeleteAddress = async (addressId) => {
    if (window.confirm('Are you sure you want to delete this address?')) {
      try {
        await deleteAddress(addressId);
        alert('Address deleted successfully!');
      } catch (error) {
        console.error('Delete address error:', error);
        alert('Failed to delete address. Please try again.');
      }
    }
  };

  const handleSetDefaultAddress = async (address) => {
    try {
      await updateAddress(address.id, { ...address, isDefault: true });
      alert('Default address updated successfully!');
    } catch (error) {
      console.error('Set default address error:', error);
      alert('Failed to set default address. Please try again.');
    }
  };

  const handleImageUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith('image/')) {
      alert('Please select an image file');
      return;
    }

    // Validate file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      alert('Image size must be less than 5MB');
      return;
    }

    setUploadingImage(true);

    try {
      const storage = getStorage();
      const storageRef = ref(storage, `users/${user.uid}/profile.jpg`);

      // Upload the file
      await uploadBytes(storageRef, file);

      // Get download URL
      const downloadURL = await getDownloadURL(storageRef);

      // Update user profile
      await updateAuthProfile(user, {
        photoURL: downloadURL
      });

      setProfileImageUrl(downloadURL);
      alert('Profile image updated successfully!');
    } catch (error) {
      console.error('Image upload error:', error);
      alert('Failed to upload image. Please try again.');
    } finally {
      setUploadingImage(false);
    }
  };

  const triggerFileInput = () => {
    fileInputRef.current?.click();
  };

  // Edit Profile sheet (mirrors iOS EditProfileView — only phone is editable)
  const openEditProfile = () => {
    setEditPhone(userPhone || user?.phoneNumber || '');
    setSheet('edit');
  };

  const handleSaveProfile = async () => {
    if (!user?.uid || !db) return;
    setSavingProfile(true);
    try {
      const { doc, setDoc } = await import('firebase/firestore');
      // Merge-only write of editable fields (mirrors iOS updateProfile)
      await setDoc(
        doc(db, 'users', user.uid),
        { phone: editPhone.trim() },
        { merge: true }
      );
      setUserPhone(editPhone.trim() || null);
      setSheet(null);
    } catch (error) {
      console.error('Save profile error:', error);
      alert('Failed to save profile. Please try again.');
    } finally {
      setSavingProfile(false);
    }
  };

  const displayName =
    `${firstName} ${lastName}`.trim() || user?.displayName || 'User';
  const displayPhone = userPhone || user?.phoneNumber || '';

  const formatAddress = (address) => {
    const line1 = `${address.street || ''}${address.apartment ? `, ${address.apartment}` : ''}`;
    const line2 = `${address.parish || ''}${address.island ? `, ${address.island}` : ''}${address.landmark ? ` • Near ${address.landmark}` : ''}`;
    return [line1, line2].filter(Boolean).join('\n');
  };

  if (!user) {
    return (
      <div className="min-h-screen bg-[#F2F2F7]">
        <ChefHeader title="My Account" />
        <div className="max-w-lg md:max-w-2xl lg:max-w-4xl mx-auto px-4 pt-16">
          <div className="flex flex-col items-center text-center">
            <User size={48} className="text-[#8E8E93] mb-3" />
            <h2 className="text-lg font-semibold text-gray-900 mb-1">Not Signed In</h2>
            <p className="text-[15px] text-[#6D6D72] mb-6">
              Please sign in to view and manage your profile.
            </p>
            <div className="w-full space-y-3">
              <button
                onClick={() => navigate('/login')}
                className="w-full py-3 rounded-xl bg-[#FF3B30] text-white font-semibold active:opacity-80"
              >
                Sign In
              </button>
              <button
                onClick={() => navigate('/signup')}
                className="w-full py-3 rounded-xl bg-white text-[#FF3B30] font-semibold shadow-sm active:bg-gray-50"
              >
                Create Account
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F2F2F7]">
      <ChefHeader title="My Account" />

      <div className="max-w-lg md:max-w-2xl lg:max-w-4xl mx-auto pb-8">
        {/* Profile card — avatar, name, email/phone, edit pencil (iOS profileContent header) */}
        <div className="px-4 mt-4">
          <div className="bg-white rounded-xl shadow-[0_1px_2px_rgba(0,0,0,0.05)] p-4">
            <div className="flex items-center">
              <button
                type="button"
                onClick={triggerFileInput}
                disabled={uploadingImage}
                className="relative w-20 h-20 shrink-0"
                aria-label="Change profile photo"
              >
                <span className="block w-20 h-20 rounded-full ring-2 ring-[#8E8E93]/30 overflow-hidden">
                  {profileImageUrl ? (
                    <img
                      src={profileImageUrl}
                      alt="Profile"
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <span className="w-full h-full bg-[#007AFF]/10 flex flex-col items-center justify-center gap-0.5">
                      <User size={26} className="text-[#007AFF]" />
                      <span className="text-[9px] text-[#6D6D72]">Tap to Upload</span>
                    </span>
                  )}
                </span>
                {uploadingImage && (
                  <span className="absolute inset-0 rounded-full bg-white/70 flex items-center justify-center">
                    <Loader2 size={22} className="animate-spin text-[#007AFF]" />
                  </span>
                )}
                <span className="absolute bottom-0 right-0 w-6 h-6 rounded-full bg-[#007AFF] flex items-center justify-center ring-2 ring-white">
                  <Camera size={12} className="text-white" />
                </span>
              </button>
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                onChange={handleImageUpload}
                className="hidden"
              />

              <div className="pl-3 flex-1 min-w-0">
                <p className="text-[17px] font-semibold text-gray-900 truncate">{displayName}</p>
                <p className="text-[15px] text-[#6D6D72] truncate">{user.email}</p>
                {displayPhone && (
                  <p className="text-[12px] text-[#6D6D72] truncate">{displayPhone}</p>
                )}
              </div>

              <button
                type="button"
                onClick={openEditProfile}
                className="shrink-0 p-1 active:opacity-60"
                aria-label="Edit profile"
              >
                <Pencil size={22} className="text-[#FF3B30]" />
              </button>
            </div>
          </div>
        </div>

        {/* Delivery Addresses (iOS "My Delivery Addresses" card) */}
        <ListSection title="My Delivery Addresses">
          {addressesLoading ? (
            <div className="px-4 py-3 min-h-[44px] flex items-center gap-2">
              <Loader2 size={16} className="animate-spin text-[#8E8E93]" />
              <span className="text-[15px] text-[#6D6D72]">Loading addresses…</span>
            </div>
          ) : addresses.length === 0 ? (
            <div className="px-4 py-3 min-h-[44px] flex items-center">
              <span className="text-[15px] text-[#6D6D72]">No saved addresses</span>
            </div>
          ) : (
            addresses.map((address) => (
              <div key={address.id} className="relative">
                <div
                  role="button"
                  tabIndex={0}
                  onClick={() => navigate(`/edit-address/${address.id}`)}
                  onKeyDown={(e) => e.key === 'Enter' && navigate(`/edit-address/${address.id}`)}
                  className="w-full flex items-start gap-3 px-4 py-3 text-left active:bg-gray-100 cursor-pointer"
                >
                  <span className="w-[30px] h-[30px] rounded-[7px] bg-[#007AFF] flex items-center justify-center shrink-0 mt-0.5">
                    <MapPin size={17} className="text-white" />
                  </span>
                  <span className="flex-1 min-w-0">
                    <span className="flex items-center gap-1.5">
                      <span className="text-[16px] font-semibold text-gray-900 truncate">
                        {address.name}
                      </span>
                      {address.isDefault && (
                        <Star size={13} className="text-[#FFCC00] fill-[#FFCC00] shrink-0" />
                      )}
                    </span>
                    <span className="block text-[14px] text-[#6D6D72] whitespace-pre-line">
                      {formatAddress(address)}
                    </span>
                    {address.instructions && (
                      <span className="block text-[12px] text-[#6D6D72] truncate">
                        {address.instructions}
                      </span>
                    )}
                  </span>
                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      setMenuFor(menuFor === address.id ? null : address.id);
                    }}
                    className="p-1 shrink-0 text-[#8E8E93] active:opacity-60"
                    aria-label="Address options"
                  >
                    <MoreHorizontal size={20} />
                  </button>
                </div>

                {menuFor === address.id && (
                  <>
                    <div className="fixed inset-0 z-30" onClick={() => setMenuFor(null)} />
                    <div className="absolute right-3 top-10 z-40 w-48 bg-white rounded-xl shadow-xl border border-black/[0.06] overflow-hidden divide-y divide-black/[0.06]">
                      {!address.isDefault && (
                        <button
                          type="button"
                          onClick={() => {
                            setMenuFor(null);
                            handleSetDefaultAddress(address);
                          }}
                          className="w-full flex items-center gap-2 px-3 py-2.5 text-left text-[15px] text-gray-900 active:bg-gray-100"
                        >
                          <Star size={15} className="text-[#8E8E93]" />
                          Set as Default
                        </button>
                      )}
                      <button
                        type="button"
                        onClick={() => {
                          setMenuFor(null);
                          handleDeleteAddress(address.id);
                        }}
                        className="w-full flex items-center gap-2 px-3 py-2.5 text-left text-[15px] text-[#FF3B30] active:bg-gray-100"
                      >
                        <Trash2 size={15} />
                        Delete
                      </button>
                    </div>
                  </>
                )}
              </div>
            ))
          )}
          <ListRow
            icon={Plus}
            iconBg="#007AFF"
            title="Add New Address"
            onClick={() => navigate('/add-address')}
          />
        </ListSection>

        {/* Payment Methods (iOS "Payment Methods" card → Manage Payment Methods row) */}
        <ListSection title="Payment Methods">
          <ListRow
            icon={CreditCard}
            iconBg="#007AFF"
            title="Manage Payment Methods"
            onClick={() => navigate('/payment-methods')}
          />
        </ListSection>

        {/* Help & Information */}
        <ListSection title="Help & Information">
          <ListRow
            icon={BookOpen}
            iconBg="#007AFF"
            title="User Guide & FAQ"
            onClick={() => navigate('/dashboard/help')}
          />
          <ListRow
            icon={HelpCircle}
            iconBg="#007AFF"
            title="Help & Support"
            onClick={() => navigate('/dashboard/support')}
          />
        </ListSection>

        {/* Legal */}
        <ListSection title="Legal">
          <ListRow
            icon={FileText}
            iconBg="#8E8E93"
            title="Terms of Service"
            onClick={() => navigate('/dashboard/terms')}
          />
          <ListRow
            icon={ShieldCheck}
            iconBg="#34C759"
            title="Privacy Policy"
            onClick={() => navigate('/dashboard/privacy')}
          />
          <ListRow
            icon={AlertTriangle}
            iconBg="#FF9500"
            title="Liability Waiver"
            onClick={() => navigate('/dashboard/liability')}
          />
        </ListSection>

        {/* Settings */}
        <ListSection title="Settings">
          <ListRow
            icon={Bell}
            iconBg="#FF9500"
            title="Notifications"
            onClick={() => navigate('/dashboard/notifications')}
          />
          <ListRow
            icon={Settings}
            iconBg="#8E8E93"
            title="Preferences & Settings"
            onClick={() => setSheet('settings')}
          />
        </ListSection>

        {/* Sign Out — iOS full-width red rounded button */}
        <div className="px-4 mt-6">
          <button
            type="button"
            onClick={handleLogout}
            className="w-full py-3 rounded-xl bg-[#FF3B30] text-white text-[17px] font-semibold active:opacity-80 flex items-center justify-center gap-2"
          >
            <LogOut size={18} />
            Sign Out
          </button>
        </div>

        {/* App version footer */}
        <p className="text-center text-[11px] text-[#8E8E93] mt-6 px-4">
          CheF SKN · v1.0.0 · Operated by Browne Ink · Support +1 (869) 660-1052
        </p>
      </div>

      {/* Edit Profile sheet — mirrors iOS EditProfileView form */}
      <Sheet
        open={sheet === 'edit'}
        onClose={() => setSheet(null)}
        detent="medium"
        title="Edit Profile"
      >
        <ListSection title="Personal Information">
          <LabeledRow label="First Name" value={firstName || user.displayName?.split(' ')[0] || '—'} />
          <LabeledRow label="Last Name" value={lastName || user.displayName?.split(' ').slice(1).join(' ') || '—'} />
          <LabeledRow label="Email" value={user.email || '—'} />
          <div className="flex items-center gap-3 px-4 py-2 min-h-[44px]">
            <span className="text-[16px] text-gray-900 shrink-0">Phone</span>
            <input
              type="tel"
              value={editPhone}
              onChange={(e) => setEditPhone(e.target.value)}
              placeholder="Phone"
              className="flex-1 min-w-0 bg-transparent text-[16px] text-right outline-none placeholder:text-[#C7C7CC]"
            />
          </div>
        </ListSection>
        <div className="px-4 mt-6 pb-6 flex gap-3">
          <button
            type="button"
            onClick={() => setSheet(null)}
            className="flex-1 py-3 rounded-xl bg-white text-gray-900 font-semibold shadow-sm active:bg-gray-50"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleSaveProfile}
            disabled={savingProfile}
            className="flex-1 py-3 rounded-xl bg-[#FF3B30] text-white font-semibold active:opacity-80 disabled:opacity-50 flex items-center justify-center gap-2"
          >
            {savingProfile && <Loader2 size={16} className="animate-spin" />}
            Save
          </button>
        </div>
      </Sheet>

      {/* Preferences & Settings sheet — mirrors iOS CustomerSettingsView */}
      <Sheet
        open={sheet === 'settings'}
        onClose={() => setSheet(null)}
        detent="medium"
        title="Preferences & Settings"
      >
        <ListSection title="App Preferences">
          <div className="flex items-center justify-between gap-3 px-4 py-3 min-h-[44px]">
            <span className="text-[16px] text-gray-900">Order Status Updates</span>
            <IosSwitch
              checked={orderNotifications}
              onChange={(v) => {
                setOrderNotifications(v);
                localStorage.setItem('orderNotifications', v ? '1' : '0');
              }}
            />
          </div>
        </ListSection>
        <ListSection title="About CheF">
          <LabeledRow label="App Version" value="1.0.0" />
          <LabeledRow label="Operated by" value="Browne Ink" />
          <LabeledRow label="Support" value="+1 (869) 660-1052" />
        </ListSection>
      </Sheet>
    </div>
  );
}

export default ProfilePage;
